import type { SupportBlock, SupportSuggestions } from '../../api/businessApps'
import './prefill.css'

/**
 * PRECOMPLETAREA SUPORTULUI pe ecran - "Complete" si "Support - <aplicatie>".
 *
 * Coordonatoarea, 6 octombrie 2026: "precompleteaza vendorii, ca tu ai unii
 * deja setati, precompleteaza tot ce poti afla despre ei". Propunerile vin de
 * la `GET /business-apps/{id}/support-suggestions` si se pun NUMAI peste
 * campurile goale, cu un semn "Suggested - from Opera PMS" langa camp. Se
 * scriu doar la Save, prin acelasi `/support`; "Clear suggestions" le scoate.
 * Semnul dispare cand omul schimba valoarea - atunci e a lui.
 */

export type SupportValues = {
  vendor: string
  portal: string
  days: number[]
  from: string
  to: string
  tz: string
  hours: string
  needs: string
}

export type SuggestedField = 'vendor' | 'portal' | 'schedule' | 'hours' | 'needs' | 'contacts'

/** Eticheta fiecarui camp propus ("from Opera PMS"); lipsa = nepropus. */
export type SuggestionMarks = Partial<Record<SuggestedField, string>>

export type ContactDraft = { name: string; role: string; phone: string; email: string; level: number; levelNote: string }

/** Formularul din fisa, fara propuneri: programul gol porneste de la L-V 09-17, Bucuresti. */
export function savedValues(s: SupportBlock): SupportValues {
  return {
    vendor: s.vendor,
    portal: s.portalUrl,
    days: s.days.length > 0 ? s.days : [1, 2, 3, 4, 5],
    from: s.from !== '' ? s.from : '09:00',
    to: s.to !== '' ? s.to : '17:00',
    tz: s.timezone !== '' ? s.timezone : 'Europe/Bucharest',
    hours: s.responseHours !== null ? String(s.responseHours) : '',
    needs: s.caseNeeds,
  }
}

/**
 * Valorile de pornire: cele din fisa, cu propunerile puse peste ce e gol.
 * `only` limiteaza campurile (fereastra Complete propune numai in pasii pe
 * care ii arata - ce nu se vede nu se scrie pe nestiute).
 */
export function withSuggestions(
  s: SupportBlock,
  suggestions: SupportSuggestions | null | undefined,
  only: Partial<Record<SuggestedField, boolean>> = {},
): { values: SupportValues; marks: SuggestionMarks; contacts: ContactDraft[] } {
  const values = savedValues(s)
  const marks: SuggestionMarks = {}
  const allowed = (f: SuggestedField) => only[f] !== false
  if (!suggestions || suggestions.ready === false) return { values, marks, contacts: [] }

  if (allowed('vendor') && suggestions.vendor && s.vendor.trim() === '') {
    values.vendor = suggestions.vendor.value
    marks.vendor = suggestions.vendor.source.label
  }
  if (allowed('portal') && suggestions.portalUrl && s.portalUrl.trim() === '') {
    values.portal = suggestions.portalUrl.value
    marks.portal = suggestions.portalUrl.source.label
  }
  const sch = suggestions.schedule
  if (allowed('schedule') && sch && s.days.length === 0 && s.from === '' && sch.days.length > 0) {
    values.days = [...sch.days].sort()
    values.from = sch.from
    values.to = sch.to
    if (sch.timezone !== '') values.tz = sch.timezone
    marks.schedule = sch.source.label
  }
  if (allowed('hours') && suggestions.responseHours && s.responseHours === null) {
    values.hours = String(suggestions.responseHours.value)
    marks.hours = suggestions.responseHours.source.label
  }
  if (allowed('needs') && suggestions.caseNeeds && s.caseNeeds.trim() === '') {
    values.needs = suggestions.caseNeeds.value
    marks.needs = suggestions.caseNeeds.source.label
  }
  let contacts: ContactDraft[] = []
  if (allowed('contacts') && suggestions.contacts && suggestions.contacts.rows.length > 0) {
    contacts = suggestions.contacts.rows.map((c) => ({ ...c }))
    marks.contacts = suggestions.contacts.source.label
  }

  return { values, marks, contacts }
}

/**
 * Care propuneri sunt inca pe ecran: semnul ramane cat valoarea e cea
 * propusa; o schimbare a omului (sau "Clear suggestions") il stinge.
 */
export function activeMarks(marks: SuggestionMarks, start: SupportValues, now: SupportValues, contactsUntouched: boolean, cleared: boolean): SuggestionMarks {
  if (cleared) return {}
  const out: SuggestionMarks = {}
  if (marks.vendor && now.vendor === start.vendor) out.vendor = marks.vendor
  if (marks.portal && now.portal === start.portal) out.portal = marks.portal
  if (marks.schedule && now.days.join(',') === start.days.join(',') && now.from === start.from && now.to === start.to && now.tz === start.tz) out.schedule = marks.schedule
  if (marks.hours && now.hours === start.hours) out.hours = marks.hours
  if (marks.needs && now.needs === start.needs) out.needs = marks.needs
  if (marks.contacts && contactsUntouched) out.contacts = marks.contacts
  return out
}

/** Semnul discret de langa un camp propus. */
export function SuggestedMark({ label, field }: { label?: string; field: SuggestedField }) {
  if (!label) return null
  return (
    <span className="app-suggested" data-suggested={field} title="Suggested from what we already know. Check it - it is saved only when you press Save.">
      <i className="fas fa-magic" aria-hidden="true"></i> Suggested - {label}
    </span>
  )
}

/** Randul de sus: ce s-a propus si "Clear suggestions". */
export function SuggestionsNote({ id, onClear }: { id: string; onClear(): void }) {
  return (
    <p className="app-suggested-note" id={id}>
      <i className="fas fa-magic" aria-hidden="true"></i> Some details are filled in from what we already know (marked <em>Suggested</em>). Check them -
      nothing is saved until you press Save.{' '}
      <button type="button" className="btn btn-link btn-xs app-suggested-clear" id={`${id}Clear`} onClick={onClear}>
        Clear suggestions
      </button>
    </p>
  )
}
